import { useEffect, useState } from 'preact/hooks';
import { api, get, post, put } from '../api';
import { Workers } from '../game/Workers';
import { Button, Card, Empty, Field, Loading, navigate, Pill, Tabs, toast, useAction, useLoad } from '../ui';
import { CastView } from '../game/CastView';
import { LocationsView } from '../game/LocationsView';
import { TimelineView } from '../game/TimelineView';
import { StoryJson } from '../game/StoryJson';
import { SetupAssistant } from '../game/SetupAssistant';

type Tab = 'write' | 'characters' | 'locations' | 'timeline' | 'json';

/** Write a story and compile it into its characters, its locations, and where each character is as the story unfolds. */
export function StoryEditor(p: { id: string }) {
	const data = useLoad(() => get(`/api/stories/${p.id}`), [p.id]);
	const [tab, setTab] = useState<Tab>('write');
	// What to select when one screen opens another (a character's location, a location's character).
	const [focus, setFocus] = useState<string | null>(null);
	const open = (t: Tab, id: string | null = null) => {
		setFocus(id);
		setTab(t);
	};
	// Poll while compiling.
	const status = data.data?.story.status ?? '';
	useEffect(() => {
		if (!status.startsWith('compiling')) return;
		const t = setInterval(data.reload, 2500);
		return () => clearInterval(t);
	}, [status]);
	if (data.error) return <main class="page"><Card title="Story not found"><p>{data.error}</p></Card></main>;
	if (!data.data) return <Loading />;
	const d = data.data;
	return (
		<main class="page wide">
			<div class="row-between">
				<h1>{d.story.title}</h1>
				<Pill tone={status === 'compiled' ? 'ok' : status.startsWith('compiling') ? 'accent' : status === 'failed' ? 'bad' : 'muted'}>{status}</Pill>
			</div>
			<Tabs
				value={tab}
				onChange={(t) => open(t)}
				tabs={[
					{ id: 'write', label: 'Write & compile' },
					{ id: 'characters', label: 'Characters', badge: d.castIssues?.length },
					{ id: 'locations', label: 'Locations', badge: d.locationIssues?.length },
					{ id: 'timeline', label: 'Character locations', badge: d.timelineIssues?.length },
					{ id: 'json', label: 'JSON' },
				]}
			/>
			{tab === 'characters' || tab === 'locations' || tab === 'timeline' ? <SetupAssistant storyId={p.id} locked={status.startsWith('compiling')} applied={data.reload} /> : null}
			{tab === 'write' ? <Write id={p.id} d={d} reload={data.reload} open={open} /> : null}
			{tab === 'characters' ? <CastView key={focus} storyId={p.id} data={d.data} issues={d.castIssues ?? []} locked={status.startsWith('compiling')} focus={focus} reload={data.reload} openLocation={d.locations ? (id) => open('locations', id) : undefined} openTimeline={d.timeline ? (id) => open('timeline', id) : undefined} /> : null}
			{tab === 'locations' ? d.locations ? <LocationsView key={focus} map={d.locations} issues={d.locationIssues ?? []} focus={focus} cast={d.cast} openCharacter={(id) => open('characters', id)} openTimeline={() => open('timeline')} /> : <Empty>Compile the story to list its locations here.</Empty> : null}
			{tab === 'json' ? <StoryJson storyId={p.id} title={d.story.title} data={d.data} compiling={status.startsWith('compiling')} reload={data.reload} /> : null}
			{tab === 'timeline' ? d.timeline && d.locations && d.cast ? <TimelineView key={focus} timeline={d.timeline} map={d.locations} cast={d.cast} issues={d.timelineIssues ?? []} focus={focus} openCharacter={(id) => open('characters', id)} openLocation={(id) => open('locations', id)} /> : <Empty>Compile the story to see where each character is as it unfolds.</Empty> : null}
		</main>
	);
}

// ------------------------------------------------------------------ write + compile

function Write(p: { id: string; d: any; reload: () => void; open: (t: Tab) => void }) {
	const [source, setSource] = useState(p.d.source ?? '');
	const [title, setTitle] = useState(p.d.story.title);
	const [provider, setProvider] = useState(p.d.story.llm?.provider ?? 'none');
	const [model, setModel] = useState(p.d.story.llm?.model ?? '');
	const [saveSrc, savingSrc] = useAction(async () => {
		await put(`/api/stories/${p.id}/source`, { title, source });
		toast('Story saved.', 'ok');
	});
	const [compile, compiling] = useAction(async () => {
		await put(`/api/stories/${p.id}/source`, { title, source });
		await put(`/api/stories/${p.id}/llm`, { provider, model });
		await post(`/api/stories/${p.id}/compile`, { fresh: !canResume });
		toast('Finding the story’s characters, locations and who is where…', 'info');
		p.reload();
	});
	const [reset, resetting] = useAction(async () => {
		if (!confirm('Clear everything compiled so far (saved steps, the characters, the locations, the character locations, the log) and start over? Your story text is kept.')) return;
		await post(`/api/stories/${p.id}/compile/reset`);
		toast('Compilation cleared.', 'ok');
		p.reload();
	});
	const [play, starting] = useAction(async () => {
		const r = await post(`/api/stories/${p.id}/play`, {});
		toast(`Game ${r.displayCode} is ready: share the code so players can join.`, 'ok');
		navigate(`/g/${r.code}`);
	});
	const status = p.d.story.status as string;
	const saved = p.d.checkpoint && !p.d.checkpoint.stale ? (p.d.checkpoint.steps as number) : 0;
	const canResume = saved > 0 && p.d.story.status === 'failed';
	const current = status.startsWith('compiling:') ? status.split(':')[1] : status === 'compiling' ? 'characters' : null;
	const editing = current === 'fix';
	return (
		<div class="two-col wide-left">
			<Card title="Your story">
				<Field label="Title">
					<input value={title} onInput={(e) => setTitle(e.currentTarget.value)} />
				</Field>
				<Field label="Story (prose)" hint="Who is in it, where it starts, the places the characters pass through, and where it can end.">
					<textarea class="story-text" rows={22} value={source} onInput={(e) => setSource(e.currentTarget.value)} />
				</Field>
				<Button variant="ghost" onClick={() => saveSrc()} busy={savingSrc}>
					Save text
				</Button>
			</Card>
			<div>
				<Card title="Compile">
					<Field label="Compiler model">
						<select value={provider} onChange={(e) => setProvider(e.currentTarget.value)}>
							<option value="none">Choose…</option>
							<option value="anthropic">Anthropic (your key)</option>
							<option value="openai">OpenAI (your key)</option>
							<option value="openrouter">OpenRouter (your key)</option>
							<option value="xai">xAI (your key)</option>
							<option value="workers-ai">Included models</option>
							<option value="local">Local worker</option>
							<option value="demo">Demo compiler (dev only; returns the sample map)</option>
						</select>
					</Field>
					<Field label="Model">
						<input value={model} onInput={(e) => setModel(e.currentTarget.value)} placeholder="e.g. claude-opus-5-5" />
					</Field>
					{provider === 'local' ? (
						<div class="callout">
							<p class="small">
								Compiling on your own machine: point the local worker at campaign id <code>story:{p.id}</code>.
							</p>
							<Workers
								status={p.d.worker}
								campaignId={`story:${p.id}`}
								add={(name) => post(`/api/stories/${p.id}/workers`, { name }).finally(p.reload)}
								revoke={(id) => api('DELETE', `/api/stories/${p.id}/workers/${id}`).finally(p.reload)}
							/>
						</div>
					) : null}
					<Button onClick={() => compile()} busy={compiling || status.startsWith('compiling')} disabled={provider === 'none'}>
						{canResume ? `Resume (${saved} steps saved)` : p.d.locations ? 'Recompile' : 'Compile'}
					</Button>
					{p.d.locations || p.d.cast || p.d.timeline || saved > 0 || p.d.compileLog?.length || status === 'failed' || status.startsWith('compiling') ? (
						<Button variant="ghost" onClick={() => reset()} busy={resetting}>
							Clear compilation &amp; start over
						</Button>
					) : null}
					{current ? <CompileSteps current={current} /> : null}
					{p.d.compileStats ? <CompileStats stats={p.d.compileStats} running={status.startsWith('compiling')} /> : null}
					{p.d.cast && p.d.locations && p.d.timeline && !current ? (
						<ul class="list small">
							<li>
								Found {p.d.cast.characters.length} characters ({p.d.cast.characters.filter((c: any) => c.tier === 'main').length} main).{' '}
								<button class="linkish" onClick={() => p.open('characters')}>
									Characters
								</button>
							</li>
							<li>
								Mapped {p.d.locations.locations.length} locations ({p.d.locations.locations.filter((l: any) => !l.parent).length} top-level).{' '}
								<button class="linkish" onClick={() => p.open('locations')}>
									Locations
								</button>
							</li>
							<li>
								Placed them across {p.d.timeline.steps.length} moments.{' '}
								<button class="linkish" onClick={() => p.open('timeline')}>
									Character locations
								</button>
							</li>
							<li>
								Gave {p.d.cast.characters.filter((c: any) => c.inventory?.length).length} characters a starting inventory, and set{' '}
								{p.d.timeline.steps.reduce((n: number, s: any) => n + s.placements.reduce((k: number, x: any) => k + (x.conditions?.length ?? 0), 0), 0)} conditions for characters to move on.{' '}
								<button class="linkish" onClick={() => p.open('characters')}>
									Edit
								</button>
							</li>
						</ul>
					) : null}
					{p.d.cast && p.d.locations && p.d.timeline && !current ? (
						<div class="callout">
							<p class="small">Ready to play: start a game, share its code, and each player takes one of the main characters.</p>
							<Button onClick={() => play()} busy={starting} disabled={provider === 'none'}>
								Play this story
							</Button>
						</div>
					) : null}
					{p.d.story.error ? <p class="bad small">{p.d.story.error}</p> : null}
					{p.d.compileLog?.length ? (
						<details>
							<summary class="small">Compile log ({p.d.compileLog.length} calls)</summary>
							<ul class="list small">
								{p.d.compileLog.map((r: any, i: number) => (
									<li key={i}>
										<details>
											<summary>
												{r.model} · <span class={r.ok ? '' : 'bad'}>{r.ok ? 'ok' : 'failed'}</span> · {r.usage.inputTokens}/{r.usage.outputTokens} tokens · {((r.ms ?? 0) / 1000).toFixed(1)}s{' '}
												{r.errors?.length ? <span class="bad">{r.errors.join('; ')}</span> : null}
											</summary>
											{r.prompt ? (
												<details>
													<summary>Last prompt message</summary>
													<pre class="mono small">{r.prompt}</pre>
												</details>
											) : null}
											{(r.responses ?? []).map((x: string, j: number) => (
												<div key={j}>
													<h4>Raw response{r.responses.length > 1 ? ` ${j + 1}` : ''} ({x.length} chars)</h4>
													<pre class="mono small">{x}</pre>
												</div>
											))}
										</details>
									</li>
								))}
							</ul>
						</details>
					) : null}
				</Card>
			</div>
		</div>
	);
}

const STEPS: Array<[string, string]> = [
	['characters', 'Characters'],
	['inventory', 'Starting inventories'],
	['locations', 'Locations'],
	['character-locations', 'Character locations'],
	['conditions', 'Progress conditions'],
];

/** Which compile step is running. */
function CompileSteps(p: { current: string }) {
	const at = Math.max(0, STEPS.findIndex(([id]) => id === p.current));
	return (
		<ol class="compile-steps small">
			{STEPS.map(([id, label], i) => (
				<li key={id} class={i < at ? 'done' : i === at ? 'on' : ''}>
					{i < at ? '✓ ' : i === at ? '… ' : ''}
					{label}
				</li>
			))}
		</ol>
	);
}

interface Stats {
	startedAt: number | null;
	ms: number;
	failures: number;
	retries: number;
	note: string | null;
	attempts: number;
	totalMs: number;
	totalFailures: number;
	totalRetries: number;
}

export function duration(ms: number): string {
	if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
	const s = Math.round(ms / 1000);
	if (s < 60) return `${s}s`;
	const m = Math.floor(s / 60);
	if (m < 60) return `${m}m ${s % 60}s`;
	return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Compile time, failures and retries: this compile, and the running totals over every compile of the story. */
function CompileStats(p: { stats: Stats; running: boolean }) {
	const s = p.stats;
	if (!p.running && !s.totalMs && !s.totalFailures) return null;
	return (
		<div class="compile-stats small">
			{s.note && p.running ? <p class="warn">{s.note}</p> : null}
			<dl>
				<dt>{p.running ? 'This compile' : 'Last compile'}</dt>
				<dd>
					{duration(s.ms)} · {plural(s.failures, 'failure')} · {plural(s.retries, 'retry', 'retries')}
				</dd>
				<dt>All compiles</dt>
				<dd>
					{duration(s.totalMs)} · {plural(s.totalFailures, 'failure')} · {plural(s.totalRetries, 'retry', 'retries')}
				</dd>
			</dl>
			<p class="muted">A failed step is tried up to {s.attempts} times, waiting 10s, 20s, 40s… between tries.</p>
		</div>
	);
}
